import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';
import './BookingModal.css';

interface BookingModalProps {
    isOpen: boolean;
    onClose: () => void;
    query: string;
    title: string;
}

export default function BookingModal({ isOpen, onClose, query, title }: BookingModalProps) {
    const { t, i18n } = useTranslation();
    const widgetRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen && (window as any).GetYourGuide) {
            // Re-initialize GYG widgets if global script is loaded
            try {
                (window as any).GetYourGuide.widgets.reinit();
            } catch (err) {
                logger.warn('Failed to reinit GYG widgets:', err);
            }
        }

        // Close on ESC
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) {
            window.addEventListener('keydown', handleEsc);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            window.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const gygLocale = i18n.language === 'ro' ? 'ro-RO' : 'en-US';

    return (
        <div className="booking-modal-overlay" onClick={onClose}>
            <div className="booking-modal-content" onClick={e => e.stopPropagation()}>
                <div className="booking-modal-header">
                    <div className="header-left">
                        <h3>{t('trip.book_tickets')}</h3>
                        <p>{title}</p>
                    </div>
                    <button className="close-booking" onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <div className="booking-modal-body">
                    {/* GetYourGuide Widget */}
                    <div
                        ref={widgetRef}
                        data-gyg-href="https://widget.getyourguide.com/default/activities.frame"
                        data-gyg-locale-code={gygLocale}
                        data-gyg-widget="activities"
                        data-gyg-number-of-items="3"
                        data-gyg-cmp="RaisaChat"
                        data-gyg-partner-id="Y9J6TKP"
                        data-gyg-q={query}
                    >
                        <span>{t('trip.powered_by')} <a target="_blank" rel="sponsored" href="https://www.getyourguide.com/">GetYourGuide</a></span>
                    </div>
                </div>

                <div className="booking-modal-footer">
                    <p>{t('trip.secure_checkout')}</p>
                </div>
            </div>
        </div>
    );
}
